import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CustomFile } from 'components/features/file-uploader/types';
import { EFileStatus } from 'components/features/file-uploader/enum';
import { UploadedFileResponseDto } from 'libs/pinnacle-sdk';

interface UseFileSessionReturn {
  fileSessionId: string;
  fileList: CustomFile[];
  setFileList: Dispatch<SetStateAction<CustomFile[]>>;
  setFileListWithInitialFileList: (files: UploadedFileResponseDto[]) => void;
}

/**
 * Custom hook for managing file session ID and file list
 * Extracts the common logic for file session management used across components
 */
export const useFileSession = (
  files?: UploadedFileResponseDto[]
): UseFileSessionReturn => {
  const [fileList, setFileList] = useState<CustomFile[]>([]);
  const [fileSessionId, setFileSessionId] = useState<string>(uuidv4());

  useEffect(() => {
    const nonLegacyFiles = files?.filter((file) => !file.isLegacyFile) ?? [];
    const fileSessionId =
      nonLegacyFiles[0]?.filePath?.split('/')?.[1] ?? uuidv4();
    setFileSessionId(fileSessionId);
  }, [files]);

  const extractCustomFileFromUploadedFile = useCallback(
    (file: UploadedFileResponseDto) => {
      const fileName = file.filePath.split('/').pop() || file.filePath;
      return {
        uid: fileName,
        status: EFileStatus.DONE,
        percent: 100,
        name: fileName,
        size: file.fileSize,
        id: file.fileId,
        originalFile: {} as File,
        legacyFilePath: file.isLegacyFile ? file.filePath : undefined,
      };
    },
    []
  );

  // Initialize file list when files prop changes
  useEffect(() => {
    if (files) {
      const initialFileList = files.map(extractCustomFileFromUploadedFile);
      setFileList(initialFileList);
    }
  }, [files, extractCustomFileFromUploadedFile]);

  const setFileListWithInitialFileList = useCallback(
    (files: UploadedFileResponseDto[]) => {
      const initialFileList = files.map(extractCustomFileFromUploadedFile);
      setFileList(initialFileList);
      setFileSessionId(files[0]?.filePath.split('/')[1] || uuidv4());
    },
    [extractCustomFileFromUploadedFile]
  );

  return {
    fileSessionId,
    fileList,
    setFileList,
    setFileListWithInitialFileList,
  };
};
